import React, { useState, useMemo } from 'react';
import { formatForDateTimeLocal } from '../../utils/dateUtils';
import { Checkbox } from '@exiftrator/shared/components/ui';

const MULTIPLE_VALUES = Symbol('MULTIPLE_VALUES');

function SelectionToolbar({
  selectedCount,
  enabledFields,
  files,
  selectedIndices,
  onEditField,
  isAllSelected,
  isSomeSelected,
  onSelectAll,
  fields,
}) {
  const [inputValues, setInputValues] = useState({});

  // Compute prefill values based on selected files
  const prefillValues = useMemo(() => {
    const values = {};
    const selectedFiles = files.filter((_, i) => selectedIndices.has(i));

    if (selectedFiles.length === 0) return values;

    enabledFields.forEach(field => {
      const fieldValues = selectedFiles.map(f => f.modifiedExifData[field.id]);
      const firstValue = fieldValues[0];
      const allSame = fieldValues.every(v => v === firstValue);

      if (allSame) {
        values[field.id] = firstValue ?? null;
      } else {
        values[field.id] = MULTIPLE_VALUES;
      }
    });

    return values;
  }, [files, selectedIndices, enabledFields]);

  // Get display value for input
  const getDisplayValue = (field) => {
    // If user has typed something, show that
    if (inputValues[field.id] !== undefined) {
      return inputValues[field.id];
    }

    const prefill = prefillValues[field.id];

    if (prefill === MULTIPLE_VALUES) {
      return ''; // Show placeholder instead
    }

    if (prefill === null || prefill === undefined) {
      return '';
    }

    // Format based on field type
    if (field.type === 'datetime-local') {
      return formatForDateTimeLocal(prefill) || '';
    }

    return String(prefill);
  };

  const getPlaceholder = (field) => {
    const prefill = prefillValues[field.id];

    if (prefill === MULTIPLE_VALUES) {
      return 'Multiple values';
    }

    if (field.type === 'gps-coordinate') {
      return '0.0000';
    }

    return '-';
  };

  const handleInputChange = (fieldId, value) => {
    setInputValues(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleInputBlur = (field) => {
    const value = inputValues[field.id];
    if (value === undefined) return;

    // Process value based on field type
    let processedValue = value;
    if (value === '') {
      processedValue = null; // Empty = clear
    } else if (field.type === 'number' || field.type === 'gps-coordinate') {
      processedValue = parseFloat(value);
    } else if (field.type === 'datetime-local') {
      processedValue = value ? new Date(value).toISOString() : null;
    }

    onEditField(field.id, processedValue);
    // Clear input after applying
    setInputValues(prev => ({ ...prev, [field.id]: undefined }));
  };

  const handleKeyDown = (e, field) => {
    if (e.key === 'Enter') {
      e.target.blur();
    } else if (e.key === 'Escape') {
      setInputValues(prev => ({ ...prev, [field.id]: undefined }));
      e.target.blur();
    }
  };

  const renderInput = (field) => {
    const value = getDisplayValue(field);
    const placeholder = getPlaceholder(field);
    const isMultiple = prefillValues[field.id] === MULTIPLE_VALUES && inputValues[field.id] === undefined;

    const baseClasses = `w-full h-7 px-2 bg-bg-deep border border-border-default rounded text-text-primary font-mono text-[11px] focus:outline-none focus:border-accent-primary/50 focus:bg-bg-tertiary transition-all placeholder:text-text-tertiary/50 ${isMultiple ? 'italic' : ''}`;

    switch (field.type) {
      case 'datetime-local':
        return (
          <input
            type="datetime-local"
            className={baseClasses}
            value={value}
            placeholder={placeholder}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            onBlur={() => handleInputBlur(field)}
            onKeyDown={(e) => handleKeyDown(e, field)}
          />
        );

      case 'gps-coordinate':
      case 'number':
        return (
          <input
            type="number"
            className={baseClasses}
            value={value}
            placeholder={placeholder}
            step={field.type === 'gps-coordinate' ? '0.000001' : 'any'}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            onBlur={() => handleInputBlur(field)}
            onKeyDown={(e) => handleKeyDown(e, field)}
          />
        );

      case 'select':
        return (
          <select
            className={baseClasses}
            value={value}
            onChange={(e) => {
              handleInputChange(field.id, e.target.value);
              const processedValue = e.target.value === '' ? null : e.target.value;
              onEditField(field.id, processedValue);
              setInputValues(prev => ({ ...prev, [field.id]: undefined }));
            }}
          >
            <option value="">{isMultiple ? 'Multiple values' : '-'}</option>
            {field.options?.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );

      case 'textarea':
      default:
        return (
          <input
            type="text"
            className={baseClasses}
            value={value}
            placeholder={placeholder}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            onBlur={() => handleInputBlur(field)}
            onKeyDown={(e) => handleKeyDown(e, field)}
          />
        );
    }
  };

  return (
    <thead className="sticky top-0 z-20">
      {/* Header row */}
      <tr className="bg-bg-tertiary">
        <th className="w-10 px-2 py-2 text-center border-b border-r border-border-default sticky left-0 z-30 bg-bg-tertiary">
          <Checkbox
            checked={isAllSelected}
            indeterminate={isSomeSelected}
            onChange={onSelectAll}
            size="sm"
          />
        </th>
        <th className="px-4 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-text-secondary border-b border-r border-border-default min-w-[80px]">Preview</th>
        <th className="px-4 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-text-secondary border-b border-r border-border-default min-w-[150px]">Filename</th>
        {fields.map((field) => (
          <th key={field.id} className={`px-4 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-text-secondary border-b border-r border-border-default min-w-[120px] ${field.category === 'location' ? 'text-accent-primary/70' : ''}`}>
            {field.label}
          </th>
        ))}
        <th className="px-4 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-text-secondary border-b border-r border-border-default w-[60px]"></th>
      </tr>

      {/* Bulk edit row */}
      <tr className="bg-bg-tertiary border-b border-accent-primary/30">
        {/* Selection count */}
        <th className="w-10 px-2 py-4 text-center border-b border-r border-border-default sticky left-0 z-30 bg-bg-tertiary">
          <div className="flex flex-col items-center gap-0.5">
            <div className="w-2 h-2 rounded-full bg-accent-primary animate-pulse" />
            <span className="text-[10px] font-bold text-accent-primary">{selectedCount}</span>
          </div>
        </th>

        {/* Preview column - empty */}
        <th className="px-4 py-4 border-b border-r border-border-default min-w-[80px]"></th>

        {/* Filename column - empty */}
        <th className="px-4 py-4 border-b border-r border-border-default min-w-[150px]"></th>

        {/* Dynamic field columns - each field in its own cell */}
        {fields.map((field) => (
          <th
            key={field.id}
            className="px-4 py-4 border-b border-r border-border-default min-w-[120px] align-middle"
          >
            {renderInput(field)}
          </th>
        ))}

        {/* Actions column - empty */}
        <th className="px-4 py-4 border-b border-r border-border-default w-[60px]"></th>
      </tr>
    </thead>
  );
}

export default SelectionToolbar;
